import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ChevronDownIcon, LightbulbIcon, XIcon } from "lucide-react";
import type { ReviewComment, ReviewCategory } from "@/types/assessment";
import { CategorySelector } from "./comment-badge";

interface CommentFormProps {
  lineNumber: number;
  filename?: string;
  onSubmit: (comment: Omit<ReviewComment, "id">) => void;
  onCancel: () => void;
  initialData?: ReviewComment;
  disabled?: boolean;
  maxLine?: number; // Maximum valid line number for validation (fix 3.7)
}

export function CommentForm({
  lineNumber,
  filename,
  onSubmit,
  onCancel,
  initialData,
  disabled = false,
  maxLine,
}: CommentFormProps) {
  const [category, setCategory] = useState<ReviewCategory | undefined>(
    initialData?.category
  );
  const [description, setDescription] = useState(initialData?.description || "");
  const [suggestion, setSuggestion] = useState(initialData?.suggestion || "");
  const [showSuggestion, setShowSuggestion] = useState(
    Boolean(initialData?.suggestion)
  );
  const [endLine, setEndLine] = useState<number | undefined>(
    initialData?.end_line
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!description.trim()) return;

      onSubmit({
        filename,
        start_line: lineNumber,
        end_line: endLine,
        category,
        description: description.trim(),
        suggestion: suggestion.trim() || undefined,
      });
    },
    [filename, lineNumber, endLine, category, description, suggestion, onSubmit]
  );

  const isValid = description.trim().length > 0;

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        "p-4 space-y-4 bg-card border rounded-lg shadow-sm",
        "animate-in fade-in slide-in-from-top-2 duration-200"
      )}
    >
      {/* Header with line info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Line</span>
          <span className="font-mono font-medium">{lineNumber}</span>
          {endLine && endLine !== lineNumber && (
            <>
              <span className="text-muted-foreground">-</span>
              <span className="font-mono font-medium">{endLine}</span>
            </>
          )}
          {/* Line range selector */}
          <button
            type="button"
            onClick={() => {
              if (endLine) {
                setEndLine(undefined);
              } else {
                setEndLine(lineNumber + 1);
              }
            }}
            disabled={disabled}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            {endLine ? "Single line" : "Add range"}
          </button>
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={disabled}
          className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
        >
          <XIcon className="size-4" />
        </button>
      </div>

      {/* End line input (if range selected) with max validation (fix 3.7) */}
      {endLine !== undefined && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">End line:</span>
          <input
            type="number"
            min={lineNumber + 1}
            max={maxLine}
            value={endLine}
            onChange={(e) => {
              const value = Number(e.target.value);
              // Clamp value within valid range
              const clampedValue = Math.max(
                lineNumber + 1,
                maxLine ? Math.min(value, maxLine) : value
              );
              setEndLine(clampedValue);
            }}
            disabled={disabled}
            className="w-20 px-2 py-1 font-mono text-sm border rounded bg-background"
            aria-label="End line number"
          />
          {maxLine && (
            <span className="text-xs text-muted-foreground">
              (max: {maxLine})
            </span>
          )}
        </div>
      )}

      {/* Category selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">
          Category
        </label>
        <CategorySelector
          value={category}
          onChange={setCategory}
          disabled={disabled}
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">
          Issue Description <span className="text-destructive">*</span>
        </label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the issue you found..."
          disabled={disabled}
          rows={3}
          className="resize-y"
          autoFocus
        />
      </div>

      {/* Suggestion (collapsible) */}
      {!showSuggestion ? (
        <button
          type="button"
          onClick={() => setShowSuggestion(true)}
          disabled={disabled}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <LightbulbIcon className="size-4" />
          Add a suggestion
          <ChevronDownIcon className="size-4" />
        </button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <LightbulbIcon className="size-4" />
              Suggestion
            </label>
            <button
              type="button"
              onClick={() => {
                setShowSuggestion(false);
                setSuggestion("");
              }}
              disabled={disabled}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Remove
            </button>
          </div>
          <Textarea
            value={suggestion}
            onChange={(e) => setSuggestion(e.target.value)}
            placeholder="Suggest how to fix the issue..."
            disabled={disabled}
            rows={2}
            className="resize-y"
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={disabled}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={disabled || !isValid}>
          {initialData ? "Update Comment" : "Add Comment"}
        </Button>
      </div>
    </form>
  );
}
